import React, { useState, useEffect, useContext } from "react";
import { Flex, Label, Autocomplete } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import { LayoutContext } from "layout/context/LayoutContext";
import { globalStrings } from "config/translations/es";
import {
  listCompanies,
  farmsByCompanyID,
  lotsByFarmID,
  lotsByCompanyID,
} from "graphql/queries";

const AgroLocationSelectors = ({
  selectedCompanyId,
  selectedFarmId,
  selectedLotId,
  onSelectCompany,
  onSelectFarm,
  onSelectLot,
}) => {
  const { me } = useContext(LayoutContext);

  const [companies, setCompanies] = useState({ list: [], loading: false });
  const [farms, setFarms] = useState({ list: [], loading: false });
  const [lots, setLots] = useState({ list: [], loading: false });

  const [companySearch, setCompanySearch] = useState("");
  const [farmSearch, setFarmSearch] = useState("");
  const [lotSearch, setLotSearch] = useState("");

  useEffect(() => {
    fetchCompanies({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (selectedCompanyId) {
      const found = companies.list.find((c) => c.id === selectedCompanyId);
      if (found) setCompanySearch(found.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedFarmId) {
      const found = farms.list.find((f) => f.id === selectedFarmId);
      if (found) setFarmSearch(found.name);
      else setFarmSearch("");
    } else {
      setFarmSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFarmId]);

  useEffect(() => {
    if (selectedLotId) {
      const found = lots.list.find((l) => l.id === selectedLotId);
      if (found) setLotSearch(found.name);
      else setLotSearch("");
    } else {
      setLotSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLotId]);

  const onCompanyInputChange = (value) => {
    setCompanySearch(value);
    fetchCompanies({ name: value });
  };

  const onFarmInputChange = (value) => {
    setFarmSearch(value);
    if (selectedCompanyId) {
      fetchFarms({ companyID: selectedCompanyId, name: value });
    }
  };

  const onLotInputChange = (value) => {
    setLotSearch(value);
    if (selectedCompanyId || selectedFarmId) {
      fetchLots({
        companyID: selectedCompanyId,
        farmID: selectedFarmId,
        name: value
      });
    }
  };

  const onSelectCompanyHandler = ({ id, label }) => {
    setCompanySearch(label);
    onSelectCompany(id);
    setFarms({ list: [], loading: false });
    setFarmSearch("");
    setLots({ list: [], loading: false });
    setLotSearch("");
    // Fetch farms for this company
    fetchFarms({ companyID: id });
  };

  const onSelectFarmHandler = ({ id, label }) => {
    setFarmSearch(label);
    onSelectFarm(id);
    setLots({ list: [], loading: false });
    setLotSearch("");
    fetchLots({ companyID: selectedCompanyId || me?.companyID, farmID: id });
  };

  const onSelectLotHandler = ({ id, label }) => {
    setLotSearch(label);
    onSelectLot(id);
  };

  const onClearCompany = () => {
    setCompanySearch("");
    onSelectCompany("");
    onSelectFarm("");
    setFarms({ list: [], loading: false });
    setFarmSearch("");
    onSelectLot("");
    setLots({ list: [], loading: false });
    setLotSearch("");
    fetchCompanies({});
  };

  const onClearFarm = () => {
    setFarmSearch("");
    onSelectFarm("");
    onSelectLot("");
    setLots({ list: [], loading: false });
    setLotSearch("");
    if (selectedCompanyId) fetchFarms({ companyID: selectedCompanyId });
  };

  const onClearLot = () => {
    setLotSearch("");
    onSelectLot("");
    if (selectedCompanyId || selectedFarmId) {
      fetchLots({ companyID: selectedCompanyId, farmID: selectedFarmId });
    }
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me?.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me?.managedCompanies?.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [{ id: { contains: id } }, { name: { contains: name } }],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchFarms({ companyID = "", id = "", name = "" }) {
    try {
      setFarms({ list: [], loading: true });
      if (!companyID) {
        setFarms({ list: [], loading: false });
        return;
      }
      const variables = { companyID };
      if (name) variables.filter = { name: { contains: name } };
      if (id) variables.filter = { id: { eq: id } };

      const farmsData = await API.graphql(
        graphqlOperation(farmsByCompanyID, variables)
      );
      const farmsItems = farmsData.data.farmsByCompanyID.items;
      setFarms((prev) => ({ ...prev, list: farmsItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setFarms((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchLots({
    companyID = "",
    farmID = "",
    id = "",
    name = "",
  }) {
    try {
      setLots({ list: [], loading: true });
      if (!companyID && !farmID) {
        setLots({ list: [], loading: false });
        return;
      }
      let variables = {};
      if (farmID) {
        variables = { farmID };
        if (name) variables.filter = { name: { contains: name } };
        if (id) variables.filter = { id: { eq: id } };
        const lotsData = await API.graphql(
          graphqlOperation(lotsByFarmID, variables)
        );
        const lotsItems = lotsData.data.lotsByFarmID.items;
        setLots((prev) => ({ ...prev, list: lotsItems }));
        return;
      }
      // fallback to company-level lots
      if (companyID) {
        variables = { companyID };
        if (name) variables.filter = { name: { contains: name } };
        if (id) variables.filter = { id: { eq: id } };
        const lotsData = await API.graphql(
          graphqlOperation(lotsByCompanyID, variables)
        );
        const lotsItems = lotsData.data.lotsByCompanyID.items;
        setLots((prev) => ({ ...prev, list: lotsItems }));
        return;
      }
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setLots((prev) => ({ ...prev, loading: false }));
    }
  }

  return (
    <>
      <Flex direction="column" gap="xs">
        <Label htmlFor="companyID">
          {strings.fields.company.label}
        </Label>
        <Autocomplete
          id="companyID"
          name="companyID"
          placeholder={strings.fields.company.placeholder}
          value={companySearch}
          options={companies.list.map((c) => ({
            id: c.id,
            label: c.name,
          }))}
          isLoading={companies.loading}
          onClear={onClearCompany}
          onChange={(e) => onCompanyInputChange(e.target.value)}
          onSelect={onSelectCompanyHandler}
          menuSlots={{ Empty: globalStrings.noResults }}
        />
      </Flex>

      <Flex direction="column" gap="xs">
        <Label htmlFor="farmID">
          {strings.fields.farm.label}
        </Label>
        <Autocomplete
          id="farmID"
          name="farmID"
          disabled={!selectedCompanyId}
          placeholder={strings.fields.farm.placeholder}
          value={farmSearch}
          options={farms.list.map((f) => ({
            id: f.id,
            label: f.name,
          }))}
          isLoading={farms.loading}
          onClear={onClearFarm}
          onChange={(e) => onFarmInputChange(e.target.value)}
          onSelect={onSelectFarmHandler}
          menuSlots={{ Empty: globalStrings.noResults }}
        />
      </Flex>

      <Flex direction="column" gap="xs">
        <Label htmlFor="lotID">
          {strings.fields.lot.label}
        </Label>
        <Autocomplete
          id="lotID"
          name="lotID"
          disabled={!selectedCompanyId || !selectedFarmId}
          placeholder={strings.fields.lot.placeholder}
          value={lotSearch}
          options={lots.list.map((l) => ({
            id: l.id,
            label: l.name,
          }))}
          isLoading={lots.loading}
          onClear={onClearLot}
          onChange={(e) => onLotInputChange(e.target.value)}
          onSelect={onSelectLotHandler}
          menuSlots={{ Empty: globalStrings.noResults }}
        />
      </Flex>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      company: { label: "Empresa", placeholder: "Buscar empresa..." },
      farm: { label: "Finca", placeholder: "Buscar finca..." },
      lot: { label: "Lote", placeholder: "Buscar lote..." },
    },
    responses: { errorFetchCompanies: "Error al consultar empresas" },
  },
});

export default AgroLocationSelectors;
